import { Box, ButtonBase, Skeleton, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import { Block } from "../../shared/Block";
import i18n from "../../../i18n";
import type { Accrual } from "../../../services/referrals";

interface RewardsListProps {
  items: Accrual[];
  isLoading: boolean;
  /**
   * Сколько записей показывать. Дефолт — 5 (для карточки на главной
   * реферальной странице). Полная страница `/referral/rewards` передаёт
   * `Infinity`, чтобы отрисовать всё.
   */
  limit?: number;
  /**
   * Показывать ли ссылку «Все →» в шапке. По умолчанию — да, но полная
   * страница выключает её (там всё уже показано).
   */
  showSeeAll?: boolean;
}

function sourceLabel(type: Accrual["source_type"], t: TFunction): string {
  const map: Record<Accrual["source_type"], string> = {
    DEPOSIT: t("rewards.source.deposit"),
    CARD_ISSUE: t("rewards.source.card_issue"),
    TRANSFER: t("rewards.source.transfer"),
    EXCHANGE: t("rewards.source.exchange"),
    SBP_PAYOUT: t("rewards.source.sbp_payout"),
    ADJUSTMENT: t("rewards.source.adjustment"),
  };
  return map[type];
}

const sourceIcons: Record<Accrual["source_type"], string> = {
  DEPOSIT: "solar:wallet-money-bold-duotone",
  CARD_ISSUE: "solar:card-bold-duotone",
  TRANSFER: "solar:transfer-horizontal-bold-duotone",
  EXCHANGE: "solar:refresh-square-bold-duotone",
  SBP_PAYOUT: "solar:scanner-2-bold-duotone",
  ADJUSTMENT: "solar:settings-bold-duotone",
};

const sourceAccent: Record<
  Accrual["source_type"],
  { color: string; bg: string }
> = {
  DEPOSIT: { color: "#3AA76D", bg: "rgba(58,167,109,0.14)" },
  CARD_ISSUE: { color: "#3377FF", bg: "rgba(51,119,255,0.14)" },
  TRANSFER: { color: "#B49BFF", bg: "rgba(180,155,255,0.14)" },
  EXCHANGE: { color: "#F0A93B", bg: "rgba(240,169,59,0.14)" },
  SBP_PAYOUT: { color: "#EF5F72", bg: "rgba(239,95,114,0.14)" },
  ADJUSTMENT: { color: "#8C93A6", bg: "rgba(140,147,166,0.14)" },
};

function formatDate(iso: string) {
  const locale = i18n.language === "en" ? "en-US" : "ru-RU";
  return new Date(iso).toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatUsdt(v: string) {
  const n = Number(v);
  const locale = i18n.language === "en" ? "en-US" : "ru-RU";
  return n.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

export function RewardsList({
  items,
  isLoading,
  limit = 5,
  showSeeAll = true,
}: RewardsListProps) {
  const { t } = useTranslation("referral");
  const navigate = useNavigate();
  const visibleItems = Number.isFinite(limit) ? items.slice(0, limit) : items;
  // Стрелку «Все →» показываем всегда, если явно не выключено, и есть хоть одна
  // запись. Бэк может отдать «всего 15 из 200» — но флаг про наличие ещё
  // записей до нас не всегда доходит, поэтому надёжнее рендерить ссылку всегда:
  // на полной странице точно всё подгрузится.
  const showArrow = showSeeAll && items.length > 0;

  if (isLoading) {
    return (
      <Block sx={{ marginTop: "10px", padding: "14px 16px" }}>
        <Skeleton height={28} sx={{ borderRadius: "10px" }} />
        <Skeleton height={44} sx={{ borderRadius: "12px", marginTop: "8px" }} />
        <Skeleton height={44} sx={{ borderRadius: "12px", marginTop: "8px" }} />
      </Block>
    );
  }

  if (items.length === 0) {
    return (
      <Block sx={{ marginTop: "10px", padding: "14px 16px" }}>
        <SectionHeader t={t} showSeeAll={false} />
        <Box
          sx={{
            marginTop: "18px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
            paddingBottom: "8px",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              backgroundColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(51,119,255,0.12)"
                  : "rgba(51,119,255,0.10)",
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon icon="solar:bill-list-bold-duotone" width={26} height={26} />
          </Box>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 600 }}>
            {t("rewards.empty_title")}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              opacity: 0.55,
              textAlign: "center",
              maxWidth: 260,
            }}
          >
            {t("rewards.empty_subtitle")}
          </Typography>
        </Box>
      </Block>
    );
  }

  return (
    <Block sx={{ marginTop: "10px", padding: "14px 16px" }}>
      <SectionHeader
        t={t}
        showSeeAll={showArrow}
        onSeeAll={() => navigate("/referral/rewards")}
      />
      <Box
        sx={{
          marginTop: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {visibleItems.map((it) => {
          const accent = sourceAccent[it.source_type];
          return (
            <Box
              key={it.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 12px",
                borderRadius: "14px",
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: "10px",
                  backgroundColor: accent.bg,
                  color: accent.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon={sourceIcons[it.source_type]}
                  width={18}
                  height={18}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "13.5px",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sourceLabel(it.source_type, t)}
                  {it.from_user_name ? ` · ${it.from_user_name}` : ""}
                </Typography>
                <Typography sx={{ fontSize: "11px", opacity: 0.55 }}>
                  {t("rewards.line_date", {
                    line: it.line_number,
                    date: formatDate(it.created_at),
                  })}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#3AA76D",
                  whiteSpace: "nowrap",
                }}
              >
                +{formatUsdt(it.amount_usdt)}
                <Typography
                  component="span"
                  sx={{
                    fontSize: "10.5px",
                    fontWeight: 500,
                    opacity: 0.6,
                    marginLeft: "3px",
                  }}
                >
                  USDT
                </Typography>
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Block>
  );
}

function SectionHeader({
  t,
  showSeeAll,
  onSeeAll,
}: {
  t: TFunction;
  showSeeAll?: boolean;
  onSeeAll?: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
          {t("rewards.section_title")}
        </Typography>
        <Typography sx={{ fontSize: "11px", opacity: 0.55, marginTop: "1px" }}>
          {t("rewards.section_subtitle")}
        </Typography>
      </Box>
      {showSeeAll && onSeeAll ? (
        <ButtonBase
          onClick={onSeeAll}
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 6px 4px 8px",
            marginTop: "-2px",
            marginRight: "-6px",
            borderRadius: "8px",
            color: "primary.main",
            fontSize: "12.5px",
            fontWeight: 600,
            "&:active": { opacity: 0.7 },
          }}
        >
          {t("rewards.see_all")}
          <Icon icon="solar:alt-arrow-right-linear" width={14} height={14} />
        </ButtonBase>
      ) : null}
    </Box>
  );
}
